import {
  FormControl,
  FormHelperText,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { IChangeEvent } from "@rjsf/core";
import UiJsonForm from "@rjsf/mui";
import {
  Experimental_DefaultFormStateBehavior,
  RegistryWidgetsType,
  RJSFSchema,
  UiSchema,
} from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import {
  ChangeEvent,
  FC,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useDynamicFormsTranslate } from "@ryde/redux";
import { ICustomField, ValidationHelpers } from "@ryde/shared";

import { MultiSelectEnumWidget } from "./rjsf-widgets/multi-select-enum.widget";
import {
  buildMultiSelectUiSchema,
  MULTI_SELECT_ENUM_WIDGET,
} from "./rjsf-widgets/rjsf-multi-select.utils";

enum JsonTabMode {
  UI = "ui",
  MANUAL = "manual",
}

interface IJsonSmartEditorTemplateProps extends ICustomField {
  schema: RJSFSchema;
  // When true (edit mode), RJSF must not inject the schema's `default:` values over the
  // saved record. Left false on create so defaults still pre-fill a brand-new record.
  skipSchemaDefaults?: boolean;
}

export const JSONSmartEditorTemplate: FC<IJsonSmartEditorTemplateProps> = ({
  name,
  formLabel,
  formAttributes,
  formValidation,
  schema,
  skipSchemaDefaults,
}) => {
  const {
    control,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });
  const formValue = getValues()[name];
  const stringifiedJson = useMemo(
    () => JSON.stringify(formValue ?? {}),
    [formValue],
  );
  const [valueToShow, setValueToShow] = useState<string>(stringifiedJson);

  const [tabMode, setTabMode] = useState<JsonTabMode>(JsonTabMode.UI);

  const jsonManualChangeHandle = (
    e: ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    try {
      const value = e.target.value;
      const parsedValue = JSON.parse(value);
      setJson(value, parsedValue);
    } catch (err) {
      setError(name, { message: t("errors.invalidJSON") });
    }
  };

  const jsonModeChangeHandle = (
    e: SyntheticEvent<Element>,
    mode: JsonTabMode,
  ): void => {
    setTabMode(mode);
  };

  const setJson = (nextValue: string, nextParsedValue: object): void => {
    try {
      setValueToShow(nextValue);
      setValue(name, nextParsedValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors(name);
    } catch (error) {
      setError(name, { message: t("errors.invalidJSON") });
    }
  };

  const jsonFormChangeHandle = (change: IChangeEvent): void => {
    const stringifiedFormData = JSON.stringify(change.formData ?? {});
    setJson(stringifiedFormData, change.formData);
  };

  useEffect(() => {
    const defaultValue = JSON.stringify(getValues(name));

    if (!defaultValue) {
      setValue(name, {});
    }
  }, [setValue, name, getValues]);

  useEffect(() => {
    setValueToShow(stringifiedJson);
  }, [stringifiedJson]);

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  const uiSchema: UiSchema = useMemo(() => {
    return {
      "ui:submitButtonOptions": {
        norender: true,
      },
      ...buildMultiSelectUiSchema(schema),
    };
  }, [schema]);

  const widgets: RegistryWidgetsType = useMemo(
    () => ({ [MULTI_SELECT_ENUM_WIDGET]: MultiSelectEnumWidget }),
    [],
  );

  // On edit, stop RJSF injecting schema `default:`s over the saved record (see note on
  // <UiJsonForm> below). Undefined on create -> RJSF keeps its default-populating behavior.
  const defaultFormStateBehavior:
    Experimental_DefaultFormStateBehavior | undefined = useMemo(
    () =>
      skipSchemaDefaults ? { emptyObjectFields: "skipDefaults" } : undefined,
    [skipSchemaDefaults],
  );

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field: { value, ...fieldRest } }) => (
          <InvisibleTextField value={value || "{}"} {...fieldRest} />
        )}
      />
      <Tabs
        value={tabMode}
        textColor="secondary"
        indicatorColor="secondary"
        onChange={jsonModeChangeHandle}
      >
        <Tab value={JsonTabMode.UI} label={t(`general.UI`)} />
        <Tab value={JsonTabMode.MANUAL} label={t(`general.MANUAL`)} />
      </Tabs>

      {tabMode === JsonTabMode.UI && (
        <UiJsonForm
          schema={schema}
          validator={validator}
          formData={formValue}
          onChange={jsonFormChangeHandle}
          widgets={widgets}
          noHtml5Validate
          /*
           * RYDE-7543: omitExtraData/liveOmit are intentionally OFF. With them on,
           * RJSF's live "omit extra data" pass fights the schema's `default:` values
           * (inject defaults -> omit -> re-inject ...) and fires onChange on every
           * render -> setValue -> re-render: an infinite loop that froze this form
           * (could not type, fields cleared). The loop is internal to RJSF's Form, so
           * stabilizing formData / guarding onChange did not help — disabling the
           * live omit is the only thing that breaks it.
           */
          liveOmit={false}
          omitExtraData={false}
          /*
           * On edit (skipSchemaDefaults), do NOT inject the schema's `default:` values
           * into the saved record's formData. RJSF's legacy `populateAllDefaults` merges
           * every `default:` over the loaded config on render; onChange then persists it
           * on save -> silent config loss when editing (e.g. Maccabiah ETLs showed provider
           * fields as defaults after new `default:`s were added upstream). `skipDefaults`
           * makes the form reflect stored data verbatim. On create it's undefined so defaults
           * still pre-fill. (Companion to the liveOmit/omitExtraData note — same `default:` root.)
           */
          experimental_defaultFormStateBehavior={defaultFormStateBehavior}
          uiSchema={uiSchema}
        />
      )}

      {tabMode === JsonTabMode.MANUAL && (
        <FormControl
          required={requiredComputed}
          data-e2e-template="json-editor-template"
          data-e2e-field-name={name}
          error={!!errors[name]}
        >
          <TextField
            label={formLabelTranslated}
            required={requiredComputed}
            fullWidth
            multiline
            rows={4}
            onChange={jsonManualChangeHandle}
            value={valueToShow || "{}"}
            data-e2e-template="json-editor-template"
            error={!!errors[name]}
          />
          {errors[name] && (
            <FormHelperText>{errors[name]?.message as string}</FormHelperText>
          )}
        </FormControl>
      )}
    </>
  );
};

const InvisibleTextField = styled(TextField)`
  display: none !important;
`;
